import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  UsersIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CogIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  BellIcon,
  FolderIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  ServerIcon,
  DocumentDuplicateIcon,
  ArchiveBoxIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '../../../contextos/autenticacion/ContextoAutenticacion'
import { usePermisos } from '../../../ganchos/usePermisos'
import { reportesService, MetricaReporte } from '../../../services/reportesService'
import { resolverPresentacionErrorApi } from '../../../services/apiError'

// Interfaces para tipado
interface EstadisticaRapida {
  id: string
  titulo: string
  valor: string | number
  icono: React.ComponentType<any>
  color: string
  cambio?: string
  tendencia?: 'up' | 'down' | 'neutral'
  descripcion?: string
}

interface ActividadReciente {
  id: string
  tipo: 'usuario_nuevo' | 'documento_subido' | 'sistema_actualizado' | 'error_reportado'
  titulo: string
  descripcion: string
  tiempo: string
  icono: React.ComponentType<any>
  color: string
  usuario?: string
}

interface DistribucionUsuariosDashboard {
  porRol: Record<string, number>
  porRolGlobal: Record<string, number>
  porTipoOrganizacion: Record<string, number>
  porRolOrganizacion: {
    institucion: Record<string, number>
    empresa: Record<string, number>
  }
}

interface AccionRapida {
  id: string
  titulo: string
  descripcion: string
  icono: React.ComponentType<any>
  color: string
  ruta: string
  gradiente: string
}

const AdminDashboardPage: React.FC = () => {
  const { usuario } = useAuth()
  const navigate = useNavigate()
  const [estadisticas, setEstadisticas] = useState<EstadisticaRapida[]>([])
  const [actividades, setActividades] = useState<ActividadReciente[]>([])
  const [distribucionUsuarios, setDistribucionUsuarios] = useState<DistribucionUsuariosDashboard>({
    porRol: {},
    porRolGlobal: {},
    porTipoOrganizacion: {},
    porRolOrganizacion: { institucion: {}, empresa: {} },
  })
  const [cargando, setCargando] = useState(true)
  const [tiempoActual, setTiempoActual] = useState(new Date())
  const [errorCarga, setErrorCarga] = useState<string | null>(null)

  // Funciones auxiliares para convertir tipos de métricas
  const getIconoParaTipo = (tipo: string) => {
    switch (tipo) {
      case 'usuarios': return UsersIcon
      case 'archivos': return DocumentTextIcon
      case 'categorias': return FolderIcon
      case 'actividad': return ChartBarIcon
      case 'almacenamiento': return ServerIcon
      default: return ChartBarIcon
    }
  }

  const getColorParaTipo = (tipo: string) => {
    switch (tipo) {
      case 'usuarios': return 'blue'
      case 'archivos': return 'green'
      case 'categorias': return 'purple'
      case 'actividad': return 'yellow'
      case 'almacenamiento': return 'indigo'
      default: return 'gray'
    }
  }

  // Función auxiliar para convertir iconos de actividades
  const getIconoParaActividad = (tipo: string) => {
    switch (tipo) {
      case 'usuario_nuevo': return UsersIcon
      case 'documento_subido': return DocumentTextIcon
      case 'sistema_actualizado': return ShieldCheckIcon
      case 'error_reportado': return ExclamationTriangleIcon
      case 'categoria_creada': return FolderIcon
      case 'archivo_eliminado': return ArchiveBoxIcon
      default: return DocumentTextIcon
    }
  }

  // Acciones rápidas
  const accionesRapidas: AccionRapida[] = [
    {
      id: 'gestionar_usuarios',
      titulo: 'Gestionar Usuarios',
      descripcion: 'Administrar usuarios y permisos',
      icono: UsersIcon,
      color: 'blue',
      ruta: '/admin/usuarios',
      gradiente: 'from-blue-500 to-blue-600'
    },
    {
      id: 'subir_documento',
      titulo: 'Subir Documento',
      descripcion: 'Agregar nuevo material educativo',
      icono: DocumentDuplicateIcon,
      color: 'green',
      ruta: '/admin/documentos/nuevo',
      gradiente: 'from-green-500 to-green-600'
    },
    {
      id: 'ver_reportes',
      titulo: 'Ver Reportes',
      descripcion: 'Analizar métricas y estadísticas',
      icono: ChartBarIcon,
      color: 'purple',
      ruta: '/admin/reportes',
      gradiente: 'from-purple-500 to-purple-600'
    },
    {
      id: 'configuracion',
      titulo: 'Configuración',
      descripcion: 'Ajustar parámetros del sistema',
      icono: CogIcon,
      color: 'gray',
      ruta: '/admin/configuracion',
      gradiente: 'from-gray-500 to-gray-600'
    }
  ]

  // Actualizar tiempo cada minuto
  useEffect(() => {
    const timer = setInterval(() => {
      setTiempoActual(new Date())
    }, 60000)

    return () => clearInterval(timer)
  }, [])

  // Cargar datos del dashboard
  useEffect(() => {
    const cargarDatos = async () => {
      setCargando(true)
      try {
        const dashboardData = await reportesService.obtenerDashboard()
        setErrorCarga(null)
        
        // Convertir métricas del servicio a estadísticas del dashboard
        const estadisticasConvertidas = dashboardData.metricas.map((metrica: MetricaReporte) => ({
          id: metrica.id,
          titulo: metrica.titulo,
          valor: metrica.valor.toString(),
          icono: getIconoParaTipo(metrica.tipo),
          color: getColorParaTipo(metrica.tipo),
          cambio: metrica.cambio ? `${metrica.cambio > 0 ? '+' : ''}${metrica.cambio}%` : undefined,
          tendencia: metrica.cambio ? (metrica.cambio > 0 ? 'up' : metrica.cambio < 0 ? 'down' : 'neutral') as 'up' | 'down' | 'neutral' : 'neutral',
          descripcion: metrica.descripcion
        }))
        
        setEstadisticas(estadisticasConvertidas)
        setDistribucionUsuarios(dashboardData.distribucionUsuarios)
        
        if (dashboardData.actividadReciente && dashboardData.actividadReciente.length > 0) {
          const actividadesConvertidas = dashboardData.actividadReciente.map((actividad: any) => ({
            ...actividad,
            icono: getIconoParaActividad(actividad.tipo),
            color: actividad.color || getColorParaTipo(actividad.tipo),
          }))
          setActividades(actividadesConvertidas)
        } else {
          setActividades([])
        }
      } catch (error) {
        console.error('Error al cargar datos del dashboard:', error)
        const presentacion = resolverPresentacionErrorApi(error, {
          tituloFallback: 'Error al cargar dashboard',
          mensajeFallback: 'No se pudo obtener la información real del panel administrativo.',
        })
        setErrorCarga(presentacion.message)
        setEstadisticas([])
        setActividades([])
        setDistribucionUsuarios({ porRol: {}, porRolGlobal: {}, porTipoOrganizacion: {}, porRolOrganizacion: { institucion: {}, empresa: {} } })
      } finally {
        setCargando(false)
      }
    }

    cargarDatos()
  }, [])

  const obtenerColorClase = (color: string, tipo: 'bg' | 'text' | 'border' = 'bg') => {
    const colores: Record<string, Record<string, string>> = {
      blue: { bg: 'bg-blue-500', text: 'text-blue-600', border: 'border-blue-200' },
      green: { bg: 'bg-green-500', text: 'text-green-600', border: 'border-green-200' },
      purple: { bg: 'bg-purple-500', text: 'text-purple-600', border: 'border-purple-200' },
      yellow: { bg: 'bg-yellow-500', text: 'text-yellow-600', border: 'border-yellow-200' },
      indigo: { bg: 'bg-indigo-500', text: 'text-indigo-600', border: 'border-indigo-200' },
      emerald: { bg: 'bg-emerald-500', text: 'text-emerald-600', border: 'border-emerald-200' },
      orange: { bg: 'bg-orange-500', text: 'text-orange-600', border: 'border-orange-200' },
      cyan: { bg: 'bg-cyan-500', text: 'text-cyan-600', border: 'border-cyan-200' },
      red: { bg: 'bg-red-500', text: 'text-red-600', border: 'border-red-200' },
      gray: { bg: 'bg-gray-500', text: 'text-gray-600', border: 'border-gray-200' }
    }
    return colores[color]?.[tipo] || colores.gray[tipo]
  }

  const bloquesDistribucion = [
    {
      titulo: 'Roles Globales',
      datos: distribucionUsuarios.porRolGlobal,
    },
    {
      titulo: 'Miembros Institucionales',
      datos: distribucionUsuarios.porRolOrganizacion.institucion,
    },
    {
      titulo: 'Miembros Empresariales',
      datos: distribucionUsuarios.porRolOrganizacion.empresa,
    },
  ]

  // Filtrar acciones rápidas según permisos
  const { tienePermiso } = usePermisos()
  const accionesFiltradas = accionesRapidas.filter(accion => {
    switch (accion.id) {
      case 'gestionar_usuarios': return tienePermiso('usuarios:ver')
      case 'subir_documento': return tienePermiso('archivos:subir')
      case 'ver_reportes': return tienePermiso('reportes:ver')
      case 'configuracion': return tienePermiso('configuracion:ver')
      default: return true
    }
  })

  if (cargando) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header del Dashboard */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <motion.h1 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-3xl md:text-4xl font-bold mb-2"
              >
                Panel de Administración
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="text-blue-100 text-lg"
              >
                Gestiona y supervisa todo el sistema LogiXport
              </motion.p>
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-4 flex items-center space-x-4 text-sm"
              >
                <div className="flex items-center space-x-2">
                  <BuildingOfficeIcon className="h-5 w-5" />
                  <span>Administrador: {usuario?.nombre || 'Admin'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <ClockIcon className="h-5 w-5" />
                  <span>
                    {tiempoActual.toLocaleDateString('es-ES', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
              </motion.div>
            </div>
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-4 md:mt-0 flex items-center space-x-2"
            >
              <div className="flex items-center space-x-2 bg-white/10 rounded-lg px-4 py-2">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm">Sistema Operativo</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {errorCarga && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200">
            {errorCarga}
          </div>
        )}

        {/* Estadísticas Rápidas */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8"
        >
          {estadisticas.filter(stat => {
            if (stat.id === 'ingresos_mensuales') return tienePermiso('financieros:ver')
            if (stat.id.startsWith('usuarios')) return tienePermiso('usuarios:ver')
            if (stat.id === 'documentos_totales') return tienePermiso('archivos:ver') || tienePermiso('contenido:ver')
            return true
          }).map((stat, index) => (
            <motion.div
              key={stat.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-center justify-between">
                <div className={`p-3 rounded-lg ${obtenerColorClase(stat.color, 'bg')} bg-opacity-10`}>
                  <stat.icono className={`h-6 w-6 ${obtenerColorClase(stat.color, 'text')}`} />
                </div>
                {stat.tendencia && (
                  <div className={`flex items-center space-x-1 text-sm ${
                    stat.tendencia === 'up' ? 'text-green-600' : 
                    stat.tendencia === 'down' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {stat.tendencia === 'up' && <ArrowTrendingUpIcon className="h-4 w-4" />}
                    {stat.tendencia === 'down' && <ArrowTrendingDownIcon className="h-4 w-4" />}
                    <span>{stat.cambio}</span>
                  </div>
                )}
              </div>
              <div className="mt-4">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{stat.valor}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{stat.titulo}</p>
                {stat.descripcion && (
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">{stat.descripcion}</p>
                )}
              </div>
            </motion.div>
          ))}
          {!estadisticas.length && (
            <div className="col-span-full rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
              No hay métricas reales disponibles todavía para este panel.
            </div>
          )}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Acciones Rápidas */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="lg:col-span-2"
          >
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Acciones Rápidas</h2>
                <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                  Ver todas
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {accionesFiltradas.map((accion, index) => (
                  <motion.button
                    key={accion.id}
                    onClick={() => navigate(accion.ruta)}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 * index }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`relative overflow-hidden bg-gradient-to-r ${accion.gradiente} text-white p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-left group`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <accion.icono className="h-8 w-8 mb-3" />
                        <h3 className="font-semibold text-lg mb-1">{accion.titulo}</h3>
                        <p className="text-sm opacity-90">{accion.descripcion}</p>
                      </div>
                      <ChevronRightIcon className="h-5 w-5 opacity-70 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Actividad Reciente */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="lg:col-span-1"
          >
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Actividad Reciente</h2>
                <BellIcon className="h-5 w-5 text-gray-400" />
              </div>
              <div className="space-y-4">
                <AnimatePresence>
                  {actividades.map((actividad, index) => (
                    <motion.div
                      key={actividad.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * index }}
                      className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                    >
                      <div className={`p-2 rounded-lg ${obtenerColorClase(actividad.color, 'bg')} bg-opacity-10 flex-shrink-0`}>
                        {actividad.icono ? (
                          <actividad.icono className={`h-4 w-4 ${obtenerColorClase(actividad.color, 'text')}`} />
                        ) : (
                          <DocumentTextIcon className={`h-4 w-4 ${obtenerColorClase(actividad.color, 'text')}`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {actividad.titulo}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          {actividad.descripcion}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {actividad.tiempo}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
                {!actividades.length && (
                  <div className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    No hay actividad reciente registrada en la base de datos.
                  </div>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium">
                  Ver todas las actividades
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-6"
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Contexto de Usuarios</h2>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300">Globales</span>
                <span className="font-semibold text-gray-900 dark:text-white">{distribucionUsuarios.porTipoOrganizacion.global || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300">Institucionales</span>
                <span className="font-semibold text-gray-900 dark:text-white">{distribucionUsuarios.porTipoOrganizacion.institucion || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300">Empresariales</span>
                <span className="font-semibold text-gray-900 dark:text-white">{distribucionUsuarios.porTipoOrganizacion.empresa || 0}</span>
              </div>
            </div>
          </div>
          {bloquesDistribucion.map((bloque) => (
            <div key={bloque.titulo} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">{bloque.titulo}</h2>
              <div className="space-y-3 text-sm">
                {Object.entries(bloque.datos).length ? Object.entries(bloque.datos).map(([clave, valor]) => (
                  <div key={clave} className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-300 capitalize">{clave}</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{valor}</span>
                  </div>
                )) : (
                  <p className="text-gray-500 dark:text-gray-400">Sin datos disponibles.</p>
                )}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Estado del Sistema */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-8 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Estado del Sistema</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <ServerIcon className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Servidor</h3>
              <p className="text-sm text-green-600">Operativo</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <GlobeAltIcon className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Base de Datos</h3>
              <p className="text-sm text-blue-600">{errorCarga ? 'Sin respuesta' : 'Conectada'}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <ShieldCheckIcon className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Seguridad</h3>
              <p className="text-sm text-purple-600">Protegido</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <ArchiveBoxIcon className="h-8 w-8 text-yellow-600" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Almacenamiento</h3>
              <p className="text-sm text-yellow-600">Sin telemetría</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default AdminDashboardPage
